/**
 * The **shared damage / death / resurrection seam** (Blackthorn's Storm Slice C,
 * PRD #289, ADR-0150).
 *
 * Before this, only the duel owned combat: it applied damage to a duelist's
 * {@link HealthComponent}, swapped a defeated player onto the ghost body, and
 * swapped them back to heal at the end - all inline in `server/gameServer.ts`.
 * The Storm's energy vortexes need the exact same three moves (hurt a player,
 * down them to a ghost, raise them back), and two independent copies of "what it
 * means to die" would inevitably drift. So the three moves are hoisted here, into
 * one pure, clock-free sim seam that **both** the duel and the Storm call, and the
 * two systems can never disagree about damage, the living<->ghost body pairing, or
 * resurrection.
 *
 * It is deliberately mechanism-only: it decides nothing about *who* may hurt whom,
 * *how much* a swing rolls, or *when* a fight ends - those stay with each caller
 * (the duel resolver, the vortex pursuit). This owns only the state transitions on
 * an entity's {@link HealthComponent} and {@link RenderableComponent}, so it reads
 * no wall clock and no unseeded randomness and unit-tests in node.
 *
 * The "remembered living body" a death must restore is parked on the downed entity
 * itself ({@link Entity.death}), not on the caller's event state, precisely so the
 * seam is self-contained: whoever calls {@link resurrect} - the duel at its death
 * beat, the Storm at event end - restores the correct body without threading it
 * back through match/event bookkeeping.
 */
import type { Entity } from './entity';
import { restoreToFull } from './health';
import { bodies, ghostBodyForLiving } from './content';

/** The fallback living body when a downed entity has no `renderable.body` (defensive; matches the duel's prior inline default). */
const DEFAULT_LIVING_BODY = bodies.get('male').body;

/** What {@link applyDamage} resolved to: the entity's health after the hit, and whether it is now down. */
export interface DamageResult {
  /** The entity's `health.current` after the hit (0 for a mobile with no health component). */
  current: number;
  /** Whether the entity is now at or below zero health (the death threshold). */
  dead: boolean;
}

/**
 * Apply `amount` damage to an entity's replicated health, clamped at zero, and
 * report whether the hit brought it down (`current <= 0`). A mobile with **no
 * health component is unharmable** - it takes no damage and never reports dead
 * (this is exactly what makes an energy vortex unkillable: it carries no
 * `health`). A negative `amount` is floored to zero, so this can only ever hurt,
 * never heal. Pure: it mutates only the health component and reads nothing else.
 */
export function applyDamage(entity: Entity, amount: number): DamageResult {
  const health = entity.health;
  if (!health) return { current: 0, dead: false };
  health.current = Math.max(0, health.current - Math.max(0, amount));
  return { current: health.current, dead: health.current <= 0 };
}

/** What {@link applyHealing} resolved to: the entity's health after the heal, and how much of it landed. */
export interface HealResult {
  /** The entity's `health.current` after the heal (0 for a mobile with no health component). */
  current: number;
  /** How much health was actually restored, after the clamp at maximum. */
  healed: number;
}

/**
 * Restore `amount` health to an entity, clamped at its maximum, and report how
 * much of it landed. The counterpart of {@link applyDamage}, and here for the
 * same reason: healing is now a combat outcome (an ability payload), and two
 * copies of "what it means to be healed" would drift the same way two copies of
 * death would.
 *
 * A mobile with **no health component is unhealable**, and a **downed entity is
 * not raised** by healing - coming back is {@link resurrect}'s job, which owns
 * the body swap a death enacted. A negative `amount` is floored to zero, so this
 * can only ever help, never hurt. Pure: it mutates only the health component.
 */
export function applyHealing(entity: Entity, amount: number): HealResult {
  const health = entity.health;
  if (!health) return { current: 0, healed: 0 };
  if (health.current <= 0) return { current: health.current, healed: 0 };
  const before = health.current;
  health.current = Math.min(health.max, health.current + Math.max(0, amount));
  return { current: health.current, healed: health.current - before };
}

/**
 * Whether an entity is currently down: it has a health component sitting at or
 * below zero. A mobile with no health is never "dead" here (it cannot be hurt).
 */
export function isDead(entity: Entity): boolean {
  return entity.health !== undefined && entity.health.current <= 0;
}

/**
 * Enact a death: swap the downed entity onto the correct-sex ghost body
 * ({@link ghostBodyForLiving}) and remember its **living** body on
 * {@link Entity.death} so {@link resurrect} can swap it back. Because the ghost
 * body rides the existing `renderable.body` id (no new flag), the snapshot
 * carries it to every client, which renders the death shroud (the ADR-0129/0136
 * pattern the duel already relied on).
 *
 * Idempotent: a second call on an already-downed entity (one that already carries
 * {@link Entity.death}) is a no-op, so it never overwrites the remembered living
 * body with the ghost body. A no-op too on an entity with no `renderable` (nothing
 * to swap). It does **not** touch health - the caller applies the killing damage
 * (via {@link applyDamage}); this is only the body/marker transition.
 */
export function enactDeath(entity: Entity): void {
  const renderable = entity.renderable;
  if (!renderable) return;
  if (entity.death) return; // already downed: keep the first-remembered living body
  const livingBody = renderable.body ?? DEFAULT_LIVING_BODY;
  entity.death = { livingBody };
  renderable.body = ghostBodyForLiving(livingBody);
}

/**
 * Resurrect an entity through the same seam a death went through: restore it to
 * full health ({@link restoreToFull}, which also clears any regen pause) and - if
 * it is currently downed ({@link Entity.death} present) - swap its remembered
 * living body back over the ghost body and clear the death marker. Safe to call on
 * a never-downed entity (heals it and does nothing else), so a caller can raise
 * "both participants" uniformly without first checking which one actually died.
 */
export function resurrect(entity: Entity): void {
  if (entity.health) restoreToFull(entity.health);
  if (entity.death) {
    if (entity.renderable) entity.renderable.body = entity.death.livingBody;
    delete entity.death;
  }
}
